import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import * as moment from 'moment-timezone';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  Firestore,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from '@angular/fire/firestore';
import { lastValueFrom } from 'rxjs';
import { Auth, signInWithCustomToken } from 'firebase/auth';
import { fireAuthFactory, FIREBASE_REFERENCES } from './methods/firebase.module';
import { ApiTokenService } from './service/api-token.service';
import { LiffServiceInterface, LiffStepReporter, SiteProfile } from './liff.service.interface';

@Injectable({
  providedIn: 'root',
})
export class LiffService implements LiffServiceInterface {
  public isInit = false;
  public liff: LIFF = liff;
  public profile: LineUserProfile = {
    userId: '',
    displayName: '',
    pictureUrl: '',
    statusMessage: '',
  };

  public initCount = 0;
  private auth!: Auth;

  constructor(
    public http: HttpClient,
    private firestore: Firestore,
    @Inject(FIREBASE_REFERENCES.TAIWAN_FIRESTORE) private readonly twFirestore: Firestore,
    private apiTokenService: ApiTokenService
  ) {}

  /**
   * 執行完整 LIFF 登入流程，維持既有文章、問卷與 route guard 的呼叫方式。
   *
   * @param liffId LINE Developers 設定的 LIFF ID。
   * @param site 目前站台代碼。
   * @param onStep 選填的子步驟計時回報函式。
   */
  async initLiff(liffId: string, site: string, onStep?: LiffStepReporter): Promise<void> {
    // 保存目前執行步驟，發生錯誤時可寫入可追蹤的錯誤紀錄。
    let step = 'liff-init';
    try {
      step = 'liff-sdk-init';
      await this.initializeLiffSdk(liffId, onStep);

      step = 'liff-login-status';

      if (this.liff.isLoggedIn()) {
        step = 'liff-get-profile';
        await this.loadLineProfile(onStep);
        step = 'firebase-auth';
        await this.authenticateFirebase(liffId, site, onStep);
      }

      step = 'dev-liff-config';

      if (this.isDevMode()) {
        const devLiffConfig = localStorage.getItem('dev-liff-config');
        console.log('dev liff cfg=', devLiffConfig);

        if (devLiffConfig) {
          this.profile = JSON.parse(localStorage.getItem('dev-liff-config') as string);
        }
      }

      step = 'completed';
    } catch (err: unknown) {
      console.error(err);
      await this.logInitError(err, { liffId, site, step });

      if (this.initCount < 2) {
        await this.initLiff(liffId, site, onStep);
      } else {
        throw new Error('此APP尚未啟用');
      }
    }
  }

  /**
   * 初始化 LIFF SDK，讓登入頁可以先判斷登入狀態，再決定是否載入 profile。
   *
   * @param liffId LINE Developers 設定的 LIFF ID。
   * @param onStep 選填的子步驟計時回報函式。
   */
  async initializeLiffSdk(liffId: string, onStep?: LiffStepReporter): Promise<void> {
    ++this.initCount;
    if (this.isInit || this.initCount >= 2) return;

    console.log('***initLiff', this.isInit);
    this.isInit = true;
    await this.measureStep('liff-sdk-init', onStep, () => this.liff.init({ liffId }));
    console.log('****isInit end', liffId);
  }

  /**
   * 取得 LINE profile，並將 decoded ID token 內的聯絡資料合併到 UI model。
   *
   * @param onStep 選填的子步驟計時回報函式。
   * @returns 已補齊可用 email 與 phone 的 LINE profile。
   */
  async loadLineProfile(onStep?: LiffStepReporter): Promise<LineUserProfile> {
    this.profile = await this.measureStep('liff-get-profile', onStep, () => this.liff.getProfile());
    // decoded ID token 提供 profile API 未回傳的 email 與電話欄位。
    const decodedIDToken = liff.getDecodedIDToken();

    sessionStorage.setItem('uid', this.profile.userId);
    if (decodedIDToken) {
      this.profile.email = decodedIDToken.email || '';
      this.profile.phone = decodedIDToken.phone_number || '';
      this.updateLineProfile(
        this.profile.userId,
        this.profile.displayName,
        this.profile.pictureUrl || '',
        this.profile.email || '',
        this.profile.phone || ''
      ).catch();
    }

    return this.profile;
  }

  /**
   * 使用 LINE ID token 換取 Firebase custom token，完成 Firebase 登入並保存 ID token。
   *
   * @param liffId 目前登入頁使用的 LIFF ID。
   * @param site 目前站台代碼。
   * @param onStep 選填的子步驟計時回報函式。
   */
  async authenticateFirebase(liffId: string, site: string, onStep?: LiffStepReporter): Promise<void> {
    // LINE ID token 用於向 Cloud Run 證明目前 LINE 使用者身分。
    const lineToken = await this.measureStep('liff-get-id-token', onStep, async () => liff.getIDToken());
    console.log('lineToken', lineToken);

    // 將 LINE ID token 放入 Authorization header，避免寫進 request body。
    const lineLogInHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${lineToken}`,
    });
    // 依目前環境組出 LINE 換發 Firebase custom token 的 API URL。
    const lineAuthUrl = `${environment.cloudrun.api}/api/line-auth-login`;
    // Firebase custom token 僅用於這次 sign-in，不寫入任何持久化紀錄。
    const { token } = await this.measureStep('cloudrun-line-auth-login', onStep, () =>
      lastValueFrom(
        this.http.post<{ status: string; token: string }>(
          lineAuthUrl,
          { site: 'elililyhcp' },
          { headers: lineLogInHeaders }
        )
      )
    );
    console.log('token', token);

    this.auth = fireAuthFactory('us');
    await this.measureStep('firebase-auth-state-ready', onStep, () => this.auth.authStateReady());
    // Firebase credential 包含登入後的使用者與取得 ID token 的能力。
    const authUser = await this.measureStep('firebase-sign-in-with-custom-token', onStep, () =>
      signInWithCustomToken(this.auth, token)
    );
    console.log('authUser', authUser);

    // 後續 API interceptor 使用 Firebase ID token 作為正式 API 憑證。
    const customToken = await this.measureStep('firebase-get-id-token', onStep, () => authUser.user.getIdToken());
    console.log('customToken', customToken);
    this.apiTokenService.setToken(customToken);
    this.liffOpenLog(liffId, site);
  }

  /**
   * 包裝 LIFF 子步驟並回報耗時，失敗時仍會回報錯誤狀態後再拋出原始錯誤。
   *
   * @param name 子步驟名稱。
   * @param onStep 選填的子步驟計時回報函式。
   * @param run 實際要執行的非同步工作。
   * @returns 非同步工作的原始結果。
   */
  private async measureStep<T>(name: string, onStep: LiffStepReporter | undefined, run: () => Promise<T>): Promise<T> {
    // 同時保留牆鐘時間與高精度時間，分別供紀錄開始點與計算耗時。
    const startedAtMs = Date.now();
    const started = performance.now();
    // finally 會依此旗標回報子步驟成功或失敗。
    let failed = false;
    try {
      return await run();
    } catch (error) {
      failed = true;
      throw error;
    } finally {
      onStep?.(name, startedAtMs, Math.round(performance.now() - started), failed);
    }
  }

  /**
   * 記錄路由守衛在 LIFF 初始化前後發生的錯誤。
   *
   * @param error 路由守衛流程拋出的原始錯誤。
   * @param context 目前的站台、LIFF ID、失敗步驟與使用者嘗試進入的路由。
   */
  async logGuardError(
    error: unknown,
    context: { site: string; liffId: string; step: string; attemptedRoute: string }
  ): Promise<void> {
    await this.logInitError(error, {
      ...context,
      event: 'liff-guard-error',
    });
  }

  /**
   * 將 LIFF 初始化或路由守衛錯誤與當下頁面、裝置資訊同步寫入 US、Taiwan Firestore。
   * 任一資料庫寫入失敗只記錄 console，不影響原始錯誤的重試流程。
   *
   * @param error LIFF 流程拋出的原始錯誤。
   * @param context 目前的 LIFF ID、站台、失敗步驟與選填的路由守衛資訊。
   */
  private async logInitError(
    error: unknown,
    context: { liffId: string; site: string; step: string; event?: string; attemptedRoute?: string }
  ): Promise<void> {
    const errorDetails = this.getErrorDetails(error);

    try {
      const logData = {
        event: context.event || 'liff-init-error',
        liffId: context.liffId || 'noLiffId',
        site: context.site || 'noSite',
        routePath: window.location.pathname,
        routeQuery: window.location.search,
        attemptedRoute: context.attemptedRoute || '',
        href: window.location.href,
        referrer: document.referrer || '',
        step: context.step,
        errorCategory: errorDetails.category,
        message: errorDetails.message,
        errorName: errorDetails.name,
        errorCode: errorDetails.code,
        httpStatus: errorDetails.httpStatus,
        httpStatusText: errorDetails.httpStatusText,
        errorJSON: errorDetails.json,
        stack: errorDetails.stack,
        initCount: this.initCount,
        isLiffInClient: this.isInClient(),
        liffOS: this.getOS(),
        liffVersion: this.getVersion(),
        lineVersion: this.getLineVersion(),
        language: this.getLanguage(),
        userAgent: window.navigator.userAgent,
        createdAt: serverTimestamp(),
      };
      const logResults = await Promise.allSettled([
        addDoc(collection(this.firestore, 'dev-log'), logData),
        addDoc(collection(this.twFirestore, 'dev-log'), logData),
      ]);

      logResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          const region = index === 0 ? 'US' : 'Taiwan';
          console.error(`Failed to write LIFF init error log to ${region} Firestore:`, result.reason);
        }
      });
    } catch (logError) {
      // 記錄失敗不可掩蓋原始 LIFF 初始化錯誤或中斷既有重試。
      console.error('Failed to write LIFF init error log:', logError);
    }
  }

  /**
   * 將 SDK、HTTP 或一般執行期錯誤整理成可搜尋的錯誤欄位。
   *
   * @param error 原始錯誤物件或未知的拋出值。
   * @returns 供 `dev-log` 使用的錯誤分類、HTTP 資訊與安全序列化內容。
   */
  private getErrorDetails(error: unknown): {
    category: string;
    message: string;
    name: string;
    code: string | number | null;
    httpStatus: number | null;
    httpStatusText: string;
    stack: string;
    json: unknown;
  } {
    const httpError = error instanceof HttpErrorResponse ? error : null;
    const errorRecord = error && typeof error === 'object' ? (error as Record<string, unknown>) : {};
    const name = error instanceof Error ? error.name : String(errorRecord['name'] || 'UnknownError');
    const message = error instanceof Error ? error.message : String(errorRecord['message'] || error || 'Unknown error');
    const httpStatus = httpError?.status ?? (typeof errorRecord['status'] === 'number' ? errorRecord['status'] : null);
    const isTimeout = name === 'LiffTimeoutError' || /timeout|timed out/i.test(message);

    return {
      category: isTimeout ? 'timeout' : httpError ? 'http-error' : 'sdk-or-runtime-error',
      message,
      name,
      code: (errorRecord['code'] as string | number | undefined) || null,
      httpStatus,
      httpStatusText: httpError?.statusText || String(errorRecord['statusText'] || ''),
      stack: error instanceof Error ? error.stack || '' : '',
      json: this.toLogSafeJson(error),
    };
  }

  /**
   * 將錯誤內容轉為可寫入 Firestore 的 JSON，並避免循環參照與敏感資料外洩。
   *
   * @param value 要序列化的原始值。
   * @returns 可安全寫入 Firestore 的 JSON 值；序列化失敗時回傳失敗原因。
   */
  private toLogSafeJson(value: unknown): unknown {
    try {
      const seen = new WeakSet<object>();
      return JSON.parse(
        JSON.stringify(value, (key, nestedValue) => {
          if (/authorization|token|password|secret/i.test(key)) {
            return '[REDACTED]';
          }

          if (nestedValue instanceof Error) {
            return Object.getOwnPropertyNames(nestedValue).reduce<Record<string, unknown>>((result, property) => {
              result[property] = nestedValue[property as keyof Error];
              return result;
            }, {});
          }

          if (nestedValue && typeof nestedValue === 'object') {
            if (seen.has(nestedValue)) {
              return '[Circular]';
            }
            seen.add(nestedValue);
          }

          return nestedValue;
        })
      );
    } catch (serializationError) {
      return { serializationError: String(serializationError), value: String(value) };
    }
  }

  private async updateLineProfile(
    userId: string,
    displayName: string,
    pictureUrl: string,
    email: string,
    phone: string
  ): Promise<void> {
    if (email || phone) {
      try {
        const userDoc = doc(this.firestore, `/lineUsers/${userId}`);
        await setDoc(
          userDoc,
          {
            displayName: displayName || '',
            pictureUrl: pictureUrl || '',
            email: email || '',
            phone: phone || '',
          },
          { merge: true }
        );
      } catch (error) {
        console.error('Error updating LINE profile:', error);
      }
    }
  }

  /**
   * 判斷是否為本機開發模式
   */
  public isDevMode() {
    return window.location.host.includes('localhost:');
  }

  /**
   * 登入 LIFF 帳號
   * @param redirectUri 登入後跳轉的網址
   */
  public login(redirectUri = window.location.href) {
    localStorage.setItem('dev-liff-login', '1');
    if (!this.isDevMode()) {
      this.liff.login({
        redirectUri,
      });
    }
  }

  /**
   * 登出 LIFF 帳號
   */
  public logout() {
    localStorage.setItem('dev-liff-login', '');
    this.apiTokenService.clearToken();
    this.liff.logout();
  }

  /**
   * 判斷登入狀態
   */
  public isLoggedIn() {
    try {
      return (this.isDevMode() && !!localStorage.getItem('dev-liff-login')) || this.liff.isLoggedIn();
    } catch (err) {
      console.log('isLogin', err);
      return false;
    }
  }

  /**
   * 判斷環境 "ios" | "android" | "web"
   */
  public getOS() {
    try {
      return this.liff.getOS();
    } catch (err) {
      return '';
    }
  }

  /**
   * 判斷LIFF的版本
   */
  public getVersion() {
    try {
      return this.liff.getVersion();
    } catch (err) {
      return '';
    }
  }

  /**
   * 判斷LIFF的版本
   */
  public getLineVersion() {
    try {
      return this.liff.getLineVersion();
    } catch (err) {
      return '';
    }
  }

  /**
   * 判斷是否為原生LIFF的環境
   */
  public isInClient() {
    try {
      return this.liff.isInClient();
    } catch (err) {
      return false;
    }
  }

  /**
   * 取得 AccessToken
   */
  public getAccessToken() {
    try {
      return this.liff.getAccessToken();
    } catch (err) {
      return '';
    }
  }

  /**
   * 取得裝置語系
   */
  public getLanguage() {
    try {
      return this.liff.getLanguage();
    } catch (err) {
      return '';
    }
  }

  /**
   * 發送並紀錄 User Log
   * @param site 必填 站台名稱
   * @param userId 必填 使用者的 userId
   * @param action 必填 事件名稱;
   * @param label 選填 自訂標籤/Key值;
   * @param value 選填 key/標籤
   * @param payload 選填 key/標籤
   */
  async log(
    site: string,
    userId: string,
    action: string,
    label?: string | number,
    value?: string | number,
    payload?: any,
    category?: 'LineLiff' | 'WebSite' | 'LineApp'
  ) {
    try {
      await this.http
        .post(`${environment.cloudrun.api}/log`, {
          site,
          userId,
          category: category || 'LineLiff',
          action,
          label,
          value,
          payload,
        })
        .toPromise();
    } catch (err) {
      console.error(err);
    }
  }

  async getClientIP(): Promise<string> {
    try {
      const result = (await this.http.get(`${environment.cloudrun.api}/get-client-ip`).toPromise()) as any;
      return result.IP || '';
    } catch (err) {
      console.error(err);
      return '';
    }
  }

  async getServerTimestamp(timezone = 'Asia/Taipei'): Promise<Date> {
    const result = (await this.http.get(`${environment.cloudrun.api}/get-server-timestamp`).toPromise()) as any;
    return moment.tz(result.timestamp || '', timezone).toDate();
  }

  /**
   * 查詢站台是否存在
   * @param site 站台名稱
   */
  async querySiteExists(site: string): Promise<boolean> {
    try {
      const siteDoc = doc(this.firestore, `sites/${site}`);
      const docSnapshot = await getDoc(siteDoc);
      return docSnapshot.exists();
    } catch (error) {
      console.error('Error checking site existence:', error);
      return false;
    }
  }

  /**
   * 查詢該 site 站台的 botId 、Liff ID 及 公開資料
   * @param site 站台名稱
   */
  async querySiteProfile(site: string): Promise<
    {
      site: string;
      botId: string;
      pictureUrl: string;
      displayName: string;
      liff: LiffConfig;
      accountId: string;
      provider: string;
    } & SiteProfile
  > {
    const result: any = await this.http.post(`${environment.cloudrun.api}/querySiteConfig`, { site }).toPromise();

    return result;
  }

  /**
   * LIFF 於開啟並初始化時，送出 LiffOpen Event
   * @param liffId
   * @param site
   */
  private liffOpenLog(liffId: string, site?: string) {
    if (this.profile.userId) {
      const url = new URL(window.location.href);
      let targetSite = '';
      try {
        targetSite = site || url.searchParams.get('site') || url.pathname.split('/')[2] || 'noSite';
      } catch (err) {
        console.error(err);
        targetSite = 'noSite';
      }
      this.log(
        targetSite,
        this.profile.userId,
        'liffOpen',
        liffId,
        window.navigator.userAgent,
        window.location.href || ''
      );
    }
  }

  async addLabels(site: string, userId: string, labels: string[], eventAction: string, eventLabel: string) {
    if (labels.length > 0) {
      const userLabels: any = {};
      for (const label of labels) {
        const labelPath = `labels.${label}`;
        userLabels[`${labelPath}.from`] = arrayUnion(site);
        userLabels[`${labelPath}.updateAt`] = new Date();
        userLabels[`${labelPath}.enable`] = true;
        userLabels[`${labelPath}.types`] = arrayUnion('liff');
      }
      try {
        const userDocRef = doc(this.firestore, `lineUsers/${userId}`);
        await updateDoc(userDocRef, userLabels);
      } catch (error) {
        console.error('Error updating labels:', error);
      }
    }
    this.log(site, userId, eventAction, eventLabel, labels.join(','));
  }

  /**
   * 若為手機則強制使用原生 LIFF 分享
   * @param messagea
   */
  async forcedLiffShare(site: string, message: any[]) {
    try {
      if (this.getOS() === 'android' || this.getOS() === 'ios') {
        // 若使用手機
        if (this.isInClient()) {
          // 已是原生 LIFF 環境
          await liff.shareTargetPicker(message);
        } else {
          // 手機的 LINE 瀏覽器 或 外部瀏覽器
          const siteProfile = await this.querySiteProfile(site);
          const encodeText = encodeURIComponent(JSON.stringify(message));
          const httpReferer = encodeURIComponent(window.location.href);
          const shareLiffUrl = `https://liff.line.me/${siteProfile.liff.shareTargetPickerLiffID}?site=${site}&messageEncode=${encodeText}`;
          window.location.href = shareLiffUrl;
        }
      } else {
        // 桌機 直接使用 SDK 分享
        if (this.isLoggedIn()) {
          await liff.shareTargetPicker(message);
        } else {
          this.login();
        }
      }
    } catch (error: any) {
      alert(error.message);
    }
  }
  async close() {
    await liff.closeWindow();
  }

  async isFollow(): Promise<boolean> {
    if (this.isDevMode()) {
      return true;
    }
    const getFriendshipResponse = await liff.getFriendship();
    return getFriendshipResponse.friendFlag ?? false;
  }
}

export function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: any;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const err = new Error(label);
      err.name = 'LiffTimeoutError';
      reject(err);
    }, ms);
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}
